import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  useJobEditData,
  useJobEditForm,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployee,
  EmployeeFilterDefault,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import {
  DotSeparator,
  DropdownMenu,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  ListOption,
  Select,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useEffect } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function JobEditTeamField({
  kind,
  label,
  required,
  placeholder,
}: {
  kind: "recruiters" | "stakeholders";
  label: string;
  required: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const data = useJobEditData();
  const { control } = useJobEditForm();
  const isRecruiters = kind === "recruiters";
  const employees = isRecruiters ? data.recruiters : data.stakeholders;
  const setEmployees = isRecruiters ? data.setRecruiters : data.setStakeholders;
  const {
    field: { value: config, onChange: setConfig },
    fieldState: { error },
  } = useController({ control, name: isRecruiters ? "recruiters_config" : "stakeholders_config" });

  const employeesSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: employees.map((x) => x.id || 0),
    me_first: true,
  });

  useEffect(() => {
    employeesSearch.setFilter((prev) => ({
      ...prev,
      exclude_ids: employees.map((e) => e.id!).filter(Boolean),
      me_first: true,
    }));
  }, [employees]);

  const getConfig = (employee: ApiEmployee) =>
    config.find((x) => x.employee_id == employee.id) || {
      employee_id: employee.id,
      can_view_salary: false,
    };

  const getCaption = (employee: ApiEmployee) => (
    <DotSeparator className={"text-secondary"}>
      {getEmployeePositionTitle(employee)}
      {getConfig(employee).can_view_salary && (
        <Text className={"text-positive"}>{t("ats:salary_visible")}</Text>
      )}
    </DotSeparator>
  );

  const getValueSlot = (employee: ApiEmployee) => {
    const employeeConfig = getConfig(employee);
    return (
      <DropdownMenu>
        <Stack gap="sm">
          <ListOption
            title={
              employeeConfig.can_view_salary
                ? t("ats:hide_candidates_salary_info")
                : t("ats:share_candidates_salary_info")
            }
            onClick={() => {
              setConfig([
                ...config.filter((x) => x.employee_id != employee.id),
                {
                  employee_id: employeeConfig.employee_id,
                  can_view_salary: !employeeConfig.can_view_salary,
                },
              ]);
            }}
            leftIcon={
              employeeConfig.can_view_salary ? (
                <FontAwesomeIcon icon={"fa-light fa-eye-slash"} />
              ) : (
                <FontAwesomeIcon icon={"fa-light fa-eye"} />
              )
            }
            captionSlot={
              employeeConfig.can_view_salary ? (
                <Text className={"text-secondary"}>
                  <span>{t("ats:hide_candidates_salary_info_caption")} </span>
                  <LinkTo to={`/settings/roles-and-permissions`} variant="primary">
                    {t("ats:hide_candidates_salary_info_caption_settings")}
                  </LinkTo>
                </Text>
              ) : (
                <Text className={"text-secondary"}>
                  {t("ats:share_candidates_salary_info_caption")}
                </Text>
              )
            }
          />
          <ListOption
            title={t("ats:remove_from_job")}
            onClick={() => {
              setEmployees(employees.filter((x) => x.id != employee.id));
              setConfig(config.filter((x) => x.employee_id != employee.id));
            }}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-times"} />}
          />
        </Stack>
      </DropdownMenu>
    );
  };

  return (
    <Stack gap={"sm"}>
      <Title header={label} size={"sm"} paddingTop required={required} />
      <Select
        label={t(isRecruiters ? "ats:add_recruiter" : "ats:add_stakeholder")}
        error={error?.message}
        emptyTitle={placeholder ?? t("common:select")}
        value={null}
        options={[]}
        searchable
        showAvatar={true}
        showCaption={true}
        onSearchOptions={(text) => employeesSearch.search(text)}
        searchOptions={employeesSearch.options}
        loadOptionsOnOpen
        onChange={(e) => {
          const addEmployee = employeesSearch.data?.items.find((x) => x.id == e);
          if (addEmployee) {
            setEmployees([...employees, addEmployee]);
            setConfig([...config, { employee_id: addEmployee.id, can_view_salary: false }]);
          }
        }}
      />
      {employees.map((employee) => (
        <ListItem
          key={`${kind}_${employee.id}`}
          behavior="list"
          title={renderEmployeeName(employee)}
          captionSlot={getCaption(employee)}
          avatarUrl={getEmployeeAvatarUrl(employee, "sm")}
          forceShowAvatar={true}
          valueSlot={getValueSlot(employee)}
        />
      ))}
    </Stack>
  );
}
